import { Button } from 'primereact/button';
import { Dispatch, FC, MutableRefObject, SetStateAction } from 'react';
import { ExitSlipModalType, setExitSlipModal } from '../../redux/reducers/exitSlipModalSlice';
import { useDispatch } from 'react-redux';
import {
  exitSlipInitialState,
  selectedExitSlipFilter,
  setExitSlipFilter,
} from '../../redux/reducers/filterExitSlipSlice';
import { LessonDate } from './ExitSlipModalContainer';
import { useMutation } from 'react-relay';
import { ExitSlipUpsertClassExitSlipMutation } from '../../graphQLOperations/__generated__/ExitSlipUpsertClassExitSlipMutation.graphql';
import { addClassExitSlip } from '../../graphQLOperations/ExitSlip';
import { useAppSelector } from '../../redux/hooks';
import { format } from 'date-fns';
import { Toast } from 'primereact/toast';

interface Props {
  modalType: ExitSlipModalType | undefined;
  lessonDates: LessonDate[];
  setLessonDates: (lessonDates: LessonDate[]) => void;
  toast: MutableRefObject<Toast | null>;
  setSaveButtonDisabled: Dispatch<SetStateAction<boolean>>;
  saveButtonDisabled: boolean;
  setApplyRefetchSnapshot: Dispatch<SetStateAction<boolean>>;
  applyRefetchSnapshot: boolean;
  refreshPanel: () => void;
}

const buttonClass = '!tw-h-11 !tw-rounded-full !tw-px-4 !tw-font-atkinson !tw-text-sm !tw-font-bold !tw-leading-5';

//PrimeReact dims a disabled button with opacity; the design uses solid grey instead
const disabledButtonClass = '!tw-bg-neutral-200 !tw-text-neutral-600 !tw-opacity-100';

const ExitSlipModalFooter: FC<Props> = ({
  modalType,
  lessonDates,
  setLessonDates,
  toast,
  setSaveButtonDisabled,
  saveButtonDisabled,
  setApplyRefetchSnapshot,
  applyRefetchSnapshot,
  refreshPanel,
}) => {
  const dispatch = useDispatch();
  const exitSlipFilterRedux = useAppSelector(selectedExitSlipFilter);
  const [upsertClassExitSlip] = useMutation<ExitSlipUpsertClassExitSlipMutation>(addClassExitSlip);

  const handleSaveButton = () => {
    if (modalType === ExitSlipModalType.ASSIGN) {
      upsertClassExitSlip({
        variables: {
          input: {
            classExitSlipInputs: exitSlipFilterRedux.classes.flatMap(cl =>
              lessonDates
                .filter(el => el.date)
                //TEAMB-3569 no id, even for a canceled ticket: re-assigning schedules the lesson
                //on a new row and leaves the canceled one, with the responses students submitted,
                //on the day that was called off
                .map(el => ({
                  curriculumLessonId: el.curriculumLessonId,
                  dueOn: format(el.date as Date, 'yyyy-MM-dd'),
                  classId: cl,
                })),
            ),
          },
        },
        onCompleted: () => {
          toast?.current?.show({
            severity: 'success',
            summary: 'Exit Slip Added',
            detail: 'The Exit Slip classes have been added',
          });

          setSaveButtonDisabled(true);
          setApplyRefetchSnapshot(true);
          //pick up the ids the server just minted so the panel edits real rows
          refreshPanel();
        },
        onError: () => {
          toast?.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: 'Something went wrong. Please try again',
          });
        },
      });
    }

    if (modalType === ExitSlipModalType.EDIT) {
      const reassigned = lessonDates.filter(el => el.isEdited).some(el => el.isReassinged);
      upsertClassExitSlip({
        variables: {
          input: {
            classExitSlipInputs: lessonDates
              .filter(el => el.isEdited)
              .map(el => ({
                curriculumLessonId: el.curriculumLessonId!,
                dueOn: format(el.date as Date, 'yyyy-MM-dd'),
                classId: exitSlipFilterRedux.classes[0]!,
                //if isReassinged, should be a new record instead an update
                ...(!el.isReassinged && { id: el.classExitSlipId }),
              })),
          },
        },
        onCompleted: () => {
          toast?.current?.show({
            severity: 'success',
            summary: 'Exit Slip Updated',
            detail: reassigned
              ? 'Exit Slip has been reassigned! You can now see it in Upcoming Lessons'
              : 'The Exit Slip classes have been updated',
          });
          setLessonDates(lessonDates.map(el => ({ ...el, isEdited: false, isReassinged: false })));
          setSaveButtonDisabled(true);
          setApplyRefetchSnapshot(true);
          //a reassign replaces the row, so without this the panel keeps editing the old id
          refreshPanel();
        },
        onError: () => {
          toast?.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: 'Something went wrong. Please try again',
          });
        },
      });
    }
  };

  return (
    <div
      className="tw-flex tw-w-full tw-justify-end tw-gap-2 tw-self-end tw-border-0 tw-border-t tw-border-solid tw-border-[#F1F0EE] tw-px-8 tw-py-5"
      style={{ position: 'relative' }}
    >
      <Button
        label="Cancel"
        outlined
        onClick={() => {
          //refetch key to trigger exit slip snapshot
          dispatch(setExitSlipModal({ visible: false, exitSlipKey: applyRefetchSnapshot ? new Date().getTime() : 0 }));
          //clean the filters
          dispatch(setExitSlipFilter(exitSlipInitialState.value));
        }}
        className={`${buttonClass} !tw-border-2 !tw-text-indigo-600 hover:!tw-text-white`}
      />
      <Button
        disabled={saveButtonDisabled}
        onClick={handleSaveButton}
        label={`${modalType === ExitSlipModalType.ASSIGN ? 'Assign exit ticket' : 'Save changes'}`}
        className={`${buttonClass} !tw-border-none ${saveButtonDisabled ? disabledButtonClass : ''}`}
      />
    </div>
  );
};

export default ExitSlipModalFooter;
